import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  Fragment,
  forwardRef,
  useCallback,
  useId,
  useMemo,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type JSX,
} from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { AuthDialog } from '@/components/auth';
import { PillButton } from '@/components/entity';
import { useAllPublicPropertyList } from '@/hooks/usePublicProperties';
import { useAuth } from '@/providers/AuthProvider';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import { buildContactInfo } from '@/lib/constants/contactInfo';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';
import defaultLogo from '@/assets/logo.jpg';

type SiteHeaderProps = HTMLAttributes<HTMLElement>;

type HeaderNavLink = {
  label: string;
  href: string;
  external: boolean;
  description?: string;
};

type HeaderNavAction = 'link' | 'call' | 'auth';
type HeaderNavActionStyle = 'link' | 'button' | 'outline';

type HeaderNavGroupMeta = {
  hasPropertyChildren: boolean;
  isLoading: boolean;
  hasError: boolean;
};

type HeaderNavItem =
  | {
      type: 'action';
      label: string;
      href: string;
      external: boolean;
      action: HeaderNavAction;
      style: HeaderNavActionStyle;
    }
  | {
      type: 'group';
      label: string;
      items: HeaderNavLink[];
      meta?: HeaderNavGroupMeta;
    };

type OrderedHeaderNavItem = HeaderNavItem & { order: number };

const COLLECTIVE_URL = 'https://tenants.strashin.com/collective';

const DEFAULT_NAV_LINKS: HeaderNavLink[] = [
  { label: 'Availabilities', href: '/availabilities', external: false },
  { label: 'The Collective', href: COLLECTIVE_URL, external: true },
  { label: 'About Us', href: '/about', external: false },
  { label: 'Contact', href: '/contact', external: false },
];

const COLLECTIVE_NAV_ITEM: HeaderNavItem = {
  type: 'action',
  label: 'The Collective',
  href: COLLECTIVE_URL,
  external: true,
  action: 'link',
  style: 'link',
};

const navLinkClassName =
  'rounded-full px-3 py-2 text-sm font-semibold text-text-primary/80 transition hover:bg-white/80 hover:text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary';

const navButtonBaseClassName =
  'inline-flex min-h-11 items-center justify-center rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

const navButtonPrimaryClassName = `${navButtonBaseClassName} bg-brand-accent text-white hover:bg-brand-accent/90 focus-visible:outline-brand-accent`;

const navButtonOutlineClassName = `${navButtonBaseClassName} border border-brand-accent/60 text-brand-accent hover:border-brand-accent hover:bg-brand-accent/10 focus-visible:outline-brand-accent`;

const mobileButtonBaseClassName =
  'flex items-center justify-center rounded-2xl px-4 py-3 text-base font-semibold shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

const mobileButtonPrimaryClassName = `${mobileButtonBaseClassName} bg-brand-accent text-white hover:bg-brand-accent/90 focus-visible:outline-brand-accent`;

const mobileButtonOutlineClassName = `${mobileButtonBaseClassName} border border-brand-accent/60 text-brand-accent hover:border-brand-accent hover:bg-brand-accent/10 focus-visible:outline-brand-accent`;

function resolveCollectiveLink(href: string): Pick<HeaderNavLink, 'href' | 'external'> {
  const normalizedHref = href.trim();
  const pathOnlyHref = normalizedHref.split(/[?#]/)[0].replace(/\/+$/, '');

  if (
    pathOnlyHref === '/collective' ||
    pathOnlyHref.startsWith('/collective/') ||
    pathOnlyHref === 'https://strashindevelopments.com/collective' ||
    pathOnlyHref.startsWith('https://strashindevelopments.com/collective/')
  ) {
    return { href: COLLECTIVE_URL, external: true };
  }

  return { href: normalizedHref, external: false };
}

export const SiteHeader = forwardRef<HTMLElement, SiteHeaderProps>(function SiteHeader(
  { className, ...rest },
  ref,
): JSX.Element {
  const { status, user, isInitializing, signOut } = useAuth();
  const location = useLocation();
  const siteSettingsQuery = useSiteSettingsDetail();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroups, setOpenMobileGroups] = useState<Record<string, boolean>>({});
  const mobileNavId = useId();
  const propertiesQuery = useAllPublicPropertyList({ pageSize: 60, vacancyOnly: false });
  const portfolioProperties = useMemo(() => {
    const items = propertiesQuery.data?.items ?? [];
    return [...items].sort((a, b) => {
      const aLabel = formatPropertyAddress(a.address);
      const bLabel = formatPropertyAddress(b.address);
      return aLabel.localeCompare(bLabel, 'en-CA', { sensitivity: 'base' });
    });
  }, [propertiesQuery.data?.items]);
  const propertiesAreLoading =
    portfolioProperties.length === 0 && (propertiesQuery.isLoading || propertiesQuery.isFetching);
  const propertiesFetchFailed = Boolean(propertiesQuery.error);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Failed to sign out', error);
    }
  }, [signOut]);

  const renderAuthActionButton = useCallback(
    ({
      label,
      variant,
      className,
      size = 'md',
    }: {
      label: string;
      variant: 'primary' | 'secondary';
      className?: string;
      size?: 'sm' | 'md';
    }) => {
      if (status === 'authenticated' && user) {
        return (
          <PillButton variant={variant} size={size} className={className} onClick={handleSignOut}>
            Log out
          </PillButton>
        );
      }

      if (isInitializing) {
        return (
          <PillButton variant={variant} size={size} className={className} disabled>
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          </PillButton>
        );
      }

      return (
        <AuthDialog
          triggerLabel={label}
          triggerVariant={variant}
          triggerSize={size}
          triggerClassName={className}
        />
      );
    },
    [handleSignOut, isInitializing, status, user],
  );

  const renderAuthControl = useCallback(
    (variant: 'inline' | 'stacked') => {
      if (status === 'authenticated' && user) {
        const label = user.displayName ?? user.email ?? 'Account';

        if (variant === 'stacked') {
          return (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-surface-subtle bg-white/90 px-4 py-3 shadow-sm">
              <div className="flex flex-col">
                <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  Signed in
                </span>
                <span className="text-base font-semibold text-text-primary">{label}</span>
              </div>
              <PillButton variant="secondary" onClick={handleSignOut} className="whitespace-nowrap">
                Log out
              </PillButton>
            </div>
          );
        }

        return (
          <Fragment>
            <span className="hidden text-sm font-medium text-text-primary/80 md:inline">
              {label}
            </span>
            <PillButton variant="secondary" onClick={handleSignOut}>
              Log out
            </PillButton>
          </Fragment>
        );
      }

      const loadingIndicator = (
        <div
          className="flex items-center gap-xs rounded-full border border-brand-primary/20 bg-white px-4 py-2 text-sm text-text-primary/70 shadow-sm"
          aria-live="polite"
        >
          <span className="inline-block h-3 w-3 animate-spin rounded-full border border-brand-primary border-t-transparent" />
          Loading
        </div>
      );

      if (variant === 'stacked') {
        return (
          <div className="space-y-3 rounded-2xl border border-surface-subtle bg-white/90 px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  Account
                </span>
                <span className="text-sm font-semibold text-text-primary">
                  Sign in or create account
                </span>
              </div>
            </div>
            <div className="relative inline-flex w-full justify-end">
              <div
                className={clsx(
                  'pointer-events-none absolute inset-0 flex items-center justify-end transition-opacity duration-150',
                  isInitializing ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden={!isInitializing}
              >
                {loadingIndicator}
              </div>
              <div
                className={clsx(
                  'transition-opacity duration-150',
                  isInitializing ? 'pointer-events-none opacity-0' : 'opacity-100',
                )}
                aria-hidden={isInitializing}
              >
                <AuthDialog />
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="relative inline-flex min-w-[8rem] justify-end">
          <div
            className={clsx(
              'pointer-events-none absolute inset-0 flex items-center justify-end transition-opacity duration-150',
              isInitializing ? 'opacity-100' : 'opacity-0',
            )}
            aria-hidden={!isInitializing}
          >
            {loadingIndicator}
          </div>
          <div
            className={clsx(
              'transition-opacity duration-150',
              isInitializing ? 'pointer-events-none opacity-0' : 'opacity-100',
            )}
            aria-hidden={isInitializing}
          >
            <AuthDialog />
          </div>
        </div>
      );
    },
    [handleSignOut, isInitializing, status, user],
  );

  const authControl = useMemo(() => renderAuthControl('inline'), [renderAuthControl]);
  const isActiveLink = useCallback(
    (href: string) => {
      const [hrefPathRaw, hrefHash] = href.split('#');
      const hrefPath = hrefPathRaw || '/';
      const { pathname, hash } = location;

      if (hrefHash) {
        return pathname === hrefPath && hash === `#${hrefHash}`;
      }

      if (hrefPath === '/') {
        return pathname === '/';
      }

      return pathname === hrefPath || pathname.startsWith(`${hrefPath}/`);
    },
    [location],
  );
  const mobileNavStyle = useMemo<CSSProperties>(
    () => ({
      transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
      opacity: isMobileMenuOpen ? 1 : 0,
      pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
      transition: 'transform 300ms ease-out, opacity 300ms ease-out',
    }),
    [isMobileMenuOpen],
  );

  const resolvedSiteName = useMemo(() => {
    const siteName = (siteSettingsQuery.data?.brand?.siteName ?? '').trim();
    return siteName.length > 0 ? siteName : 'Strashin Developments';
  }, [siteSettingsQuery.data?.brand?.siteName]);

  const resolvedTagline = useMemo(() => {
    const tagline = (siteSettingsQuery.data?.brand?.tagline ?? '').trim();
    return tagline.length > 0 ? tagline : 'Leasing & Workspaces';
  }, [siteSettingsQuery.data?.brand?.tagline]);

  const contactPhone = useMemo(
    () => buildContactInfo(siteSettingsQuery.data).phone,
    [siteSettingsQuery.data],
  );
  const callHref = useMemo(() => `tel:${contactPhone.replace(/[^\d+]/g, '')}`, [contactPhone]);
  const callAriaLabel = useMemo(
    () => `Call ${resolvedSiteName} at ${contactPhone}`,
    [contactPhone, resolvedSiteName],
  );

  const resolvedLogoSrc = useMemo(() => {
    const configuredLogo = (siteSettingsQuery.data?.brand?.logoUrl ?? '').trim();
    return configuredLogo.length > 0 ? configuredLogo : defaultLogo;
  }, [siteSettingsQuery.data?.brand?.logoUrl]);

  const navigationItems = useMemo<HeaderNavItem[]>(() => {
    const links = siteSettingsQuery.data?.navigation?.links ?? [];
    const propertyMap = new Map(portfolioProperties.map((property) => [property.id, property]));

    const buildGroupItems = (
      children: (typeof links)[number]['children'],
    ): { items: HeaderNavLink[]; hasDropdown: boolean; meta?: HeaderNavGroupMeta } => {
      const rawChildren = children ?? [];
      const hasPropertyChildren = rawChildren.some(
        (child) => (child.propertyId ?? '').trim().length > 0,
      );
      const items = rawChildren
        .filter((child) => Boolean(child.enabled))
        .map((child, childIndex) => {
          const order = Number.isFinite(child.order) ? child.order : childIndex + 1;
          const propertyId = (child.propertyId ?? '').trim();
          if (propertyId.length > 0) {
            const property = propertyMap.get(propertyId);
            if (!property) {
              return null;
            }
            const addressLabel = formatPropertyAddress(property.address);
            const label = child.label.trim() || addressLabel;
            const slug = property.propertyCode.trim() || property.id;
            const submarket = (property.submarket ?? '').trim();
            return {
              label,
              href: `/properties/${slug.toLowerCase()}`,
              external: false,
              description: submarket.length > 0 ? submarket : undefined,
              order,
            };
          }

          const label = child.label.trim();
          const href = child.href.trim();
          if (label.length === 0 || href.length === 0) {
            return null;
          }
          const collectiveLink = resolveCollectiveLink(href);
          return {
            label,
            href: collectiveLink.href,
            external: collectiveLink.external || Boolean(child.external),
            order,
          };
        })
        .filter((child): child is HeaderNavLink & { order: number } => child !== null)
        .sort((a, b) => a.order - b.order)
        .map(({ order: _order, ...item }) => item);

      return {
        items,
        hasDropdown: rawChildren.length > 0,
        meta: hasPropertyChildren
          ? {
              hasPropertyChildren,
              isLoading: propertiesAreLoading,
              hasError: propertiesFetchFailed,
            }
          : undefined,
      };
    };

    const configuredItems = links
      .filter((entry) => Boolean(entry.enabled))
      .map((entry, index): OrderedHeaderNavItem | null => {
        const label = entry.label.trim();
        const href = entry.href.trim();
        const order = Number.isFinite(entry.order) ? entry.order : index + 1;
        const action: HeaderNavAction =
          entry.action === 'call' || entry.action === 'auth' ? entry.action : 'link';
        const style: HeaderNavActionStyle =
          entry.style === 'button' || entry.style === 'outline' ? entry.style : 'link';
        const { items: children, hasDropdown, meta } = buildGroupItems(entry.children);

        if (label.length === 0) {
          return null;
        }

        if (hasDropdown) {
          return {
            type: 'group' as const,
            label,
            items: children,
            meta,
            order,
          };
        }

        if (href.length > 0) {
          const collectiveLink = resolveCollectiveLink(href);
          return {
            type: 'action' as const,
            label,
            href: collectiveLink.href,
            external: collectiveLink.external || Boolean(entry.external),
            action,
            style,
            order,
          };
        }

        if (action === 'call' || action === 'auth') {
          return {
            type: 'action' as const,
            label,
            href,
            external: false,
            action,
            style,
            order,
          };
        }

        return null;
      })
      .filter((entry): entry is OrderedHeaderNavItem => entry !== null)
      .sort((a, b) => a.order - b.order)
      .map(({ order: _order, ...item }) => item);

    if (configuredItems.length > 0) {
      const hasCollective = configuredItems.some(
        (item) => item.type === 'action' && item.href === COLLECTIVE_URL,
      );
      return hasCollective ? configuredItems : [...configuredItems, COLLECTIVE_NAV_ITEM];
    }

    const fallbackProperties = portfolioProperties.map((property) => {
      const addressLabel = formatPropertyAddress(property.address);
      const slug = property.propertyCode.trim() || property.id;
      const submarket = (property.submarket ?? '').trim();
      return {
        label: addressLabel,
        href: `/properties/${slug.toLowerCase()}`,
        external: false,
        description: submarket.length > 0 ? submarket : undefined,
      };
    });
    const fallbackGroup: HeaderNavItem = {
      type: 'group',
      label: 'Properties',
      items: fallbackProperties,
      meta: {
        hasPropertyChildren: true,
        isLoading: propertiesAreLoading,
        hasError: propertiesFetchFailed,
      },
    };

    return [
      fallbackGroup,
      ...DEFAULT_NAV_LINKS.map((item) => ({
        type: 'action' as const,
        ...item,
        action: 'link' as const,
        style: 'link' as const,
      })),
    ];
  }, [
    siteSettingsQuery.data?.navigation?.links,
    portfolioProperties,
    propertiesAreLoading,
    propertiesFetchFailed,
  ]);

  const resolvedMobileTitle = useMemo(() => {
    const title = (siteSettingsQuery.data?.navigation?.mobileTitle ?? '').trim();
    return title.length > 0 ? title : 'Browse Strashin';
  }, [siteSettingsQuery.data?.navigation?.mobileTitle]);

  const resolvedMobileDescription = useMemo(() => {
    const description = (siteSettingsQuery.data?.navigation?.mobileDescription ?? '').trim();
    return description.length > 0 ? description : 'Jump to sections or manage your account.';
  }, [siteSettingsQuery.data?.navigation?.mobileDescription]);

  const resolvedMobileShortcutHeading = useMemo(() => {
    const heading = (siteSettingsQuery.data?.navigation?.mobileShortcutHeading ?? '').trim();
    return heading.length > 0 ? heading : 'Need a shortcut?';
  }, [siteSettingsQuery.data?.navigation?.mobileShortcutHeading]);

  const resolvedMobileShortcutDescription = useMemo(() => {
    const description = (
      siteSettingsQuery.data?.navigation?.mobileShortcutDescription ?? ''
    ).trim();
    return description.length > 0 ? description : 'Browse availability or contact the team.';
  }, [siteSettingsQuery.data?.navigation?.mobileShortcutDescription]);

  const resolvedMobileShortcutPrimary = useMemo(() => {
    const shortcut = siteSettingsQuery.data?.navigation?.mobileShortcutPrimary;
    const label = (shortcut?.label ?? '').trim();
    const href = (shortcut?.href ?? '').trim();

    return {
      label: label.length > 0 ? label : 'View availability',
      href: href.length > 0 ? href : '/availabilities',
    } as const;
  }, [
    siteSettingsQuery.data?.navigation?.mobileShortcutPrimary?.href,
    siteSettingsQuery.data?.navigation?.mobileShortcutPrimary?.label,
  ]);

  const resolvedMobileShortcutSecondary = useMemo(() => {
    const shortcut = siteSettingsQuery.data?.navigation?.mobileShortcutSecondary;
    const label = (shortcut?.label ?? '').trim();
    const href = (shortcut?.href ?? '').trim();

    return {
      label: label.length > 0 ? label : 'Contact',
      href: href.length > 0 ? href : '/contact',
    } as const;
  }, [
    siteSettingsQuery.data?.navigation?.mobileShortcutSecondary?.href,
    siteSettingsQuery.data?.navigation?.mobileShortcutSecondary?.label,
  ]);

  return (
    <Dialog.Root
      open={isMobileMenuOpen}
      onOpenChange={(open) => {
        setIsMobileMenuOpen(open);
        if (!open) {
          setOpenMobileGroups({});
        }
      }}
    >
      <header
        ref={ref}
        {...rest}
        data-testid="site-header"
        className={clsx(
          'sticky top-0 z-40 border-b border-surface-subtle bg-surface-cream/95 backdrop-blur supports-[backdrop-filter]:bg-surface-cream/80',
          className,
        )}
      >
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-brand-primary focus:shadow-md"
        >
          Skip to content
        </a>
        <div className="container relative flex items-center justify-center gap-3 py-0 md:justify-between md:gap-4 md:py-0">
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-surface-subtle bg-white text-text-primary shadow-sm transition hover:-translate-y-[calc(50%-2px)] hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary md:hidden"
              aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={isMobileMenuOpen}
              aria-controls={mobileNavId}
            >
              <span className="sr-only">Toggle navigation</span>
              <span className="relative flex h-4 w-5 items-center justify-center">
                <span
                  className={clsx(
                    'absolute inset-x-0 top-0 h-[2px] rounded-full bg-text-primary transition-all duration-200',
                    isMobileMenuOpen ? 'translate-y-2 rotate-45' : '',
                  )}
                />
                <span
                  className={clsx(
                    'absolute inset-x-0 h-[2px] rounded-full bg-text-primary transition-opacity duration-150',
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-100',
                  )}
                  aria-hidden
                />
                <span
                  className={clsx(
                    'absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-text-primary transition-all duration-200',
                    isMobileMenuOpen ? '-translate-y-2 -rotate-45' : '',
                  )}
                />
              </span>
            </button>
          </Dialog.Trigger>

          <a
            href={callHref}
            className="absolute left-3 top-1/2 inline-flex min-h-11 -translate-y-1/2 items-center justify-center rounded-full bg-brand-accent px-3 text-xs font-semibold uppercase tracking-wide text-white shadow-sm transition hover:bg-brand-accent/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent md:hidden"
            aria-label={callAriaLabel}
            title={contactPhone}
          >
            Call
          </a>

          <div className="flex items-center gap-3 md:gap-4">
            <a
              href="/"
              className="inline-flex items-center gap-3 rounded-full border border-transparent px-2 py-1 transition hover:border-surface-subtle"
              aria-label={`${resolvedSiteName} home`}
            >
              <img
                src={resolvedLogoSrc}
                alt={`${resolvedSiteName} logo`}
                className="h-10 w-auto rounded-md shadow-sm sm:h-12 sm:w-[140px] md:h-16 md:w-[170px] lg:h-20 lg:w-[200px]"
              />
              <span className="hidden text-left text-xs font-semibold uppercase tracking-[0.08em] text-text-primary/70 sm:block">
                {resolvedSiteName}
                <span className="block text-[0.7rem] font-medium text-text-primary/60">
                  {resolvedTagline}
                </span>
              </span>
            </a>
          </div>

          <nav aria-label="Primary navigation" className="hidden items-center gap-4 md:flex">
            <div className="flex items-center gap-2 lg:gap-3">
              {navigationItems.map((item, index) => {
                if (item.type === 'group') {
                  const isGroupActive = item.items.some(
                    (child) => !child.external && isActiveLink(child.href),
                  );
                  const groupStatusMessage = item.meta?.hasPropertyChildren
                    ? item.meta.isLoading
                      ? 'Loading properties...'
                      : item.meta.hasError
                        ? 'Unable to load properties.'
                        : 'No properties configured.'
                    : null;

                  return (
                    <DropdownMenu.Root key={`${item.label}-${String(index)}`} modal={false}>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          className={clsx(
                            navLinkClassName,
                            isGroupActive && 'bg-white text-brand-primary shadow-sm',
                            'inline-flex items-center gap-1.5',
                          )}
                          aria-label={`${item.label} menu`}
                        >
                          {item.label}
                          <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          align="start"
                          sideOffset={10}
                          className="z-50 box-border max-h-[var(--radix-popper-available-height)] w-[min(20rem,calc(100vw-2rem))] overflow-y-auto overflow-x-hidden rounded-2xl border border-surface-subtle bg-white p-2 shadow-2xl focus:outline-none"
                        >
                          {item.items.length === 0 && groupStatusMessage ? (
                            <div className="px-3 py-2 text-sm font-semibold text-text-primary/70">
                              {groupStatusMessage}
                            </div>
                          ) : (
                            item.items.map((child) => (
                              <DropdownMenu.Item key={`${child.href}-${child.label}`} asChild>
                                <a
                                  href={child.href}
                                  className={clsx(
                                    'rounded-xl px-3 py-2 text-sm font-semibold text-text-primary/80 outline-none transition hover:bg-surface-subtle/60 focus:bg-surface-subtle/60',
                                    child.description
                                      ? 'flex flex-col gap-0.5'
                                      : 'flex items-center justify-between',
                                  )}
                                  rel={child.external ? 'noreferrer' : undefined}
                                  target={child.external ? '_blank' : undefined}
                                >
                                  <span className="text-sm font-semibold text-text-primary">
                                    {child.label}
                                  </span>
                                  {child.description ? (
                                    <span className="text-xs font-medium text-text-primary/60">
                                      {child.description}
                                    </span>
                                  ) : null}
                                </a>
                              </DropdownMenu.Item>
                            ))
                          )}
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  );
                }

                const trimmedHref = item.href.trim();
                const isActive =
                  item.action === 'link' && !item.external && isActiveLink(trimmedHref);
                const isLinkStyle = item.style === 'link';
                const actionHref =
                  item.action === 'call'
                    ? trimmedHref.startsWith('tel:')
                      ? trimmedHref
                      : callHref
                    : trimmedHref;

                if (item.action === 'auth') {
                  const variant = item.style === 'button' ? 'primary' : 'secondary';
                  const className =
                    item.style === 'link'
                      ? clsx(navLinkClassName, 'border-transparent bg-transparent')
                      : undefined;

                  return (
                    <Fragment key={`${item.label}-${String(index)}`}>
                      {renderAuthActionButton({
                        label: item.label,
                        variant,
                        className,
                      })}
                    </Fragment>
                  );
                }

                return (
                  <a
                    key={`${item.label}-${String(index)}`}
                    href={actionHref}
                    className={clsx(
                      isLinkStyle
                        ? navLinkClassName
                        : item.style === 'button'
                          ? navButtonPrimaryClassName
                          : navButtonOutlineClassName,
                      isLinkStyle && isActive && 'bg-white text-brand-primary shadow-sm',
                    )}
                    aria-current={isActive ? 'page' : undefined}
                    rel={item.action === 'link' && item.external ? 'noreferrer' : undefined}
                    target={item.action === 'link' && item.external ? '_blank' : undefined}
                  >
                    {item.label}
                  </a>
                );
              })}
            </div>
            <div className="hidden items-center gap-3 md:flex">
              <a
                href={callHref}
                className="inline-flex min-h-11 items-center justify-center rounded-full bg-brand-accent px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-accent/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
                aria-label={callAriaLabel}
                title={contactPhone}
              >
                <span>Call</span>
                <span className="ml-2 hidden lg:inline">{contactPhone}</span>
              </a>
              {authControl}
            </div>
          </nav>
        </div>
      </header>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden" />
        <Dialog.Content
          forceMount
          id={mobileNavId}
          className={clsx(
            'fixed left-0 right-6 top-0 z-50 flex h-[100dvh] flex-col overflow-y-auto bg-surface-cream px-5 pb-8 pt-4 shadow-2xl will-change-transform focus:outline-none motion-reduce:transition-none md:hidden',
          )}
          style={{ ...mobileNavStyle, overflowY: 'auto' }}
          data-testid="site-header-mobile-nav"
        >
          <div className="flex items-center justify-between gap-4 border-b border-surface-subtle pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm">
                <img
                  src={resolvedLogoSrc}
                  alt={`${resolvedSiteName} logo`}
                  className="h-8 w-auto rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <Dialog.Title className="text-base font-semibold text-text-primary">
                  {resolvedMobileTitle}
                </Dialog.Title>
                <Dialog.Description className="text-sm text-text-primary/70">
                  {resolvedMobileDescription}
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-surface-subtle bg-white text-text-primary shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                aria-label="Close navigation"
              >
                <span className="sr-only">Close navigation</span>
                <span className="relative flex h-4 w-4 items-center justify-center">
                  <span className="absolute h-[2px] w-4 rotate-45 rounded-full bg-text-primary" />
                  <span className="absolute h-[2px] w-4 -rotate-45 rounded-full bg-text-primary" />
                </span>
              </button>
            </Dialog.Close>
          </div>

          <div className="mt-6 space-y-5">
            <div className="grid gap-3">
              {navigationItems.map((item, index) => {
                if (item.type === 'group') {
                  const groupId = `${item.label}-${String(index)}`;
                  const isGroupActive = item.items.some(
                    (child) => !child.external && isActiveLink(child.href),
                  );
                  const isGroupOpen = Boolean(openMobileGroups[groupId]);
                  const groupStatusMessage = item.meta?.hasPropertyChildren
                    ? item.meta.isLoading
                      ? 'Loading properties...'
                      : item.meta.hasError
                        ? 'Unable to load properties.'
                        : 'No properties configured.'
                    : null;

                  return (
                    <div key={groupId} className="grid gap-2">
                      <button
                        type="button"
                        className={clsx(
                          'flex items-center justify-between rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base font-semibold text-text-primary shadow-sm transition hover:-translate-y-0.5 hover:border-brand-primary/50 hover:shadow-md',
                          (isGroupActive || isGroupOpen) &&
                            'border-brand-primary/60 bg-white shadow-md',
                        )}
                        aria-expanded={isGroupOpen}
                        onClick={() => {
                          setOpenMobileGroups((previous) => ({
                            ...previous,
                            [groupId]: !previous[groupId],
                          }));
                        }}
                      >
                        <span>{item.label}</span>
                        <span
                          aria-hidden
                          className={clsx(
                            'text-lg text-brand-primary transition-transform',
                            isGroupOpen ? 'rotate-90' : null,
                          )}
                        >
                          &gt;
                        </span>
                      </button>

                      {isGroupOpen ? (
                        <div className="rounded-2xl border border-surface-subtle bg-white p-3 shadow-sm">
                          {item.items.length === 0 && groupStatusMessage ? (
                            <p className="px-2 py-3 text-sm font-semibold text-text-primary/70">
                              {groupStatusMessage}
                            </p>
                          ) : (
                            <div className="grid gap-2">
                              {item.items.map((child) => (
                                <a
                                  key={`${child.href}-${child.label}`}
                                  href={child.href}
                                  className="flex items-center justify-between rounded-xl border border-surface-subtle bg-surface-subtle/40 px-3 py-2 text-sm font-semibold text-text-primary transition hover:bg-surface-subtle/70"
                                  rel={child.external ? 'noreferrer' : undefined}
                                  target={child.external ? '_blank' : undefined}
                                  onClick={() => {
                                    setIsMobileMenuOpen(false);
                                  }}
                                >
                                  <span className="flex min-w-0 flex-col">
                                    <span className="truncate">{child.label}</span>
                                    {child.description ? (
                                      <span className="text-xs font-medium text-text-primary/60">
                                        {child.description}
                                      </span>
                                    ) : null}
                                  </span>
                                  <span aria-hidden className="text-brand-primary">
                                    &gt;
                                  </span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                }

                const trimmedHref = item.href.trim();
                const isActive =
                  item.action === 'link' && !item.external && isActiveLink(trimmedHref);
                const isLinkStyle = item.style === 'link';
                const actionHref =
                  item.action === 'call'
                    ? trimmedHref.startsWith('tel:')
                      ? trimmedHref
                      : callHref
                    : trimmedHref;

                if (item.action === 'auth') {
                  const variant = item.style === 'button' ? 'primary' : 'secondary';
                  const className = clsx(
                    item.style === 'link'
                      ? clsx(navLinkClassName, 'border-transparent bg-transparent')
                      : item.style === 'button'
                        ? mobileButtonPrimaryClassName
                        : mobileButtonOutlineClassName,
                    'w-full',
                  );

                  return (
                    <div key={`${item.label}-${String(index)}`}>
                      {renderAuthActionButton({
                        label: item.label,
                        variant,
                        className,
                      })}
                    </div>
                  );
                }

                return (
                  <a
                    key={`${item.label}-${String(index)}`}
                    href={actionHref}
                    className={clsx(
                      isLinkStyle
                        ? 'flex items-center justify-between rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base font-semibold text-text-primary shadow-sm transition hover:-translate-y-0.5 hover:border-brand-primary/50 hover:shadow-md'
                        : item.style === 'button'
                          ? mobileButtonPrimaryClassName
                          : mobileButtonOutlineClassName,
                      isLinkStyle && isActive && 'border-brand-primary/60 bg-white shadow-md',
                    )}
                    aria-current={isActive ? 'page' : undefined}
                    rel={item.action === 'link' && item.external ? 'noreferrer' : undefined}
                    target={item.action === 'link' && item.external ? '_blank' : undefined}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <span>{item.label}</span>
                    {isLinkStyle ? (
                      <span aria-hidden className="text-lg text-brand-primary">
                        &gt;
                      </span>
                    ) : null}
                  </a>
                );
              })}
            </div>

            <div className="rounded-2xl border border-surface-subtle bg-white/90 px-4 py-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    {resolvedMobileShortcutHeading}
                  </span>
                  <span className="text-sm text-text-primary/80">
                    {resolvedMobileShortcutDescription}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={resolvedMobileShortcutPrimary.href}
                    className="min-h-11 rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                    rel={
                      resolvedMobileShortcutPrimary.href.startsWith('http')
                        ? 'noreferrer'
                        : undefined
                    }
                    target={
                      resolvedMobileShortcutPrimary.href.startsWith('http') ? '_blank' : undefined
                    }
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {resolvedMobileShortcutPrimary.label}
                  </a>
                  <a
                    href={resolvedMobileShortcutSecondary.href}
                    className="min-h-11 rounded-full border border-brand-primary/40 px-4 py-2 text-sm font-semibold text-brand-primary transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                    rel={
                      resolvedMobileShortcutSecondary.href.startsWith('http')
                        ? 'noreferrer'
                        : undefined
                    }
                    target={
                      resolvedMobileShortcutSecondary.href.startsWith('http') ? '_blank' : undefined
                    }
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {resolvedMobileShortcutSecondary.label}
                  </a>
                </div>
              </div>
            </div>

            {renderAuthControl('stacked')}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});
